import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '../views/layout/Login.vue'
import Layout from '../views/layout/Layout.vue'
import { fetchMenuTree } from '../api/modules/menu'
import { getMenuComponentPath } from '../utils/menuComponent'
import { isMobileDevice } from '../utils/device'

Vue.use(VueRouter)

const ROOT_ROUTE_NAME = 'RootLayout'

const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录' }
  }
]

function createRouter() {
  return new VueRouter({
    mode: 'history',
    base: '/',
    routes: constantRoutes
  })
}

const router = createRouter()

let dynamicRoutesReady = false
let dynamicRouteSignature = ''

/**
 * 归一化菜单里的组件路径，得到相对 `src/views/` 的路径。
 * 兼容 `../views/xxx`、`/views/xxx`、`views/xxx`、`xxx` 等历史写法。
 * @param {string} componentPath 菜单声明的组件路径
 * @returns {string} 归一化后的相对路径（可能为空串）
 */
function normalizeComponentPath(componentPath) {
  if (!componentPath) {
    return ''
  }

  let normalized = String(componentPath).replace(/\\/g, '/').trim()
  normalized = normalized.replace(/^\.\.\/views\//, '')
  normalized = normalized.replace(/^\.\/views\//, '')
  normalized = normalized.replace(/^views\//, '')
  normalized = normalized.replace(/^\/+/, '')

  if (!normalized) {
    return ''
  }

  if (!normalized.endsWith('.vue')) {
    normalized = `${normalized.replace(/\/+$/, '')}/index.vue`
  }

  return normalized
}

/**
 * 由组件路径生成懒加载函数。
 * 使用 webpack 动态 import，配合 webpackInclude 保证所有 views 下的 .vue 被打包。
 * @param {string} componentPath 归一化后的相对路径
 * @returns {Function} 组件加载函数
 */
function loadView(componentPath) {
  return () => import(
    /* webpackInclude: /\.vue$/ */
    `../views/${componentPath}`
  )
}

/**
 * 生成路由名。菜单 id 唯一，优先使用；否则回退到 path 派生。
 * @param {Object} menu 菜单节点
 * @returns {string} 路由名
 */
function createRouteName(menu) {
  if (menu.id) {
    return `menu_${menu.id}`
  }

  return menu.path
    .split('/')
    .filter(Boolean)
    .map(segment => segment.replace(/[^a-zA-Z0-9]/g, '_'))
    .join('_') || 'dynamic_route'
}

/**
 * 解析菜单对应的路由组件。
 * 组件路径完全由菜单的 componentPath 决定，不再维护本地静态路由映射表。
 * @param {Object} menu 菜单节点
 * @returns {Function|null} 组件加载函数，目录节点返回 null
 */
function resolveMenuComponent(menu) {
  const componentPath = normalizeComponentPath(getMenuComponentPath(menu))
  return componentPath ? loadView(componentPath) : null
}

/**
 * 递归收集菜单树中所有可注册为路由的叶子节点。
 * 目录节点（无 componentPath）本身不注册路由，仅作为层级容器存在。
 * @param {Array} menuTree 菜单树
 * @param {Array} routes 收集结果
 * @returns {Array} 路由数组
 */
function collectDynamicChildren(menuTree, routes = []) {
  menuTree.forEach(menu => {
    const children = Array.isArray(menu.children) ? menu.children : []
    const component = resolveMenuComponent(menu)

    if (component) {
      routes.push({
        path: menu.path,
        name: createRouteName(menu),
        component,
        meta: {
          ...(menu.meta || {}),
          title: menu.title,
          menuId: menu.id
        }
      })
    }

    if (children.length) {
      collectDynamicChildren(children, routes)
    }
  })

  return routes
}

/**
 * 构建挂载在 Layout 下的一级路由。
 * @param {Array} menuTree 菜单树
 * @returns {Object} 根布局路由
 */
function buildLayoutRoute(menuTree) {
  return {
    path: '/',
    name: ROOT_ROUTE_NAME,
    component: Layout,
    redirect: '/home',
    children: collectDynamicChildren(menuTree)
  }
}

function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
  dynamicRoutesReady = false
}

/**
 * 拉取菜单树。菜单的 componentPath 即路由组件路径的唯一来源。
 * @returns {Promise<Array>} 菜单树
 */
async function fetchMenuTreeForRoutes() {
  const menuTree = await fetchMenuTree()
  return Array.isArray(menuTree) ? menuTree : []
}

/**
 * 初始化动态路由。
 * @param {boolean} force 是否强制重建（菜单增删改后调用）
 * @returns {Promise<Array>} 菜单树
 */
export async function initDynamicRoutes(force = false) {
  const menuTree = await fetchMenuTreeForRoutes()
  const signature = JSON.stringify(menuTree)

  if (!force && dynamicRoutesReady && signature === dynamicRouteSignature) {
    return menuTree
  }

  resetRouter()
  router.addRoutes([buildLayoutRoute(menuTree)])
  dynamicRoutesReady = true
  dynamicRouteSignature = signature

  return menuTree
}

/**
 * 供页面过滤等场景复用菜单树，避免重复请求。
 * @returns {Promise<Array>} 菜单树
 */
export async function getMenuTree() {
  return initDynamicRoutes()
}

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token')

  // ================= 移动端展示模式 =================
  // 手机/平板（分辨率 <=768 或移动 UA）访问站点时：
  //   1. 免登录直接展示万象棋排行 / 收件箱（这两个模块的数据接口都无需登录）；
  //   2. 万象棋是手机默认落地页；收件箱作为显式目标路径时同样放行，互不抢占；
  //   3. 其余任何地址都会被强制重定向回默认落地页。
  if (isMobileDevice()) {
    // 手机端允许免登录直访的独立模块路径
    const mobileStandaloneTargets = ['/app/wzwxq', '/app/inbox', '/app/third-party-model']
    const mobileStandaloneTarget = mobileStandaloneTargets.includes(to.path)
    // 默认落地页：保持原有行为，仍是万象棋排行
    const mobileDefaultTarget = '/app/wzwxq'

    if (!mobileStandaloneTarget && to.name !== 'Login') {
      try {
        if (!dynamicRoutesReady) await initDynamicRoutes()
        next({ path: mobileDefaultTarget, replace: true })
      } catch (error) {
        console.error('Failed to init routes for mobile mode:', error)
        next({ name: 'Login' })
      }
      return
    }
    if (mobileStandaloneTarget && !token) {
      if (!dynamicRoutesReady) {
        try {
          await initDynamicRoutes()
          next({ ...to, replace: true })
        } catch (error) {
          console.error('Failed to init routes for mobile target:', error)
          next({ name: 'Login' })
        }
        return
      }
      next()
      return
    }
    // 其余（mobileStandaloneTarget 且已登录、或登录页兜底）继续走下方原有逻辑
  }

  // freeView 参数：访问带 freeView 的模块时（如 /ai/llm-knowledge?freeView=1），
  // 未登录用户也允许进入当前模块（由 Layout 隐藏其余菜单）。
  if (to.query && Object.prototype.hasOwnProperty.call(to.query, 'freeView')) {
    if (!token && !dynamicRoutesReady) {
      // 首次进入：未登录需先把动态路由（含当前模块）注册好，再重导航一次以命中新路由。
      try {
        await initDynamicRoutes()
      } catch (error) {
        console.error('Failed to init routes for freeView access:', error)
        next({ name: 'Login' })
        return
      }
      next({ ...to, replace: true })
      return
    }

    // 未登录且路由已就绪 → 直接放行当前模块；已登录则继续走下方正常逻辑。
    if (!token) {
      next()
      return
    }
  }

  if (to.name !== 'Login' && !token) {
    next({ name: 'Login' })
    return
  }

  if (token && !dynamicRoutesReady) {
    try {
      await initDynamicRoutes()
      if (to.name === 'Login') {
        /**
         * 已登录用户访问登录页时，动态路由初始化完成后直接进入首页。
         */
        next({ path: '/home', replace: true })
        return
      }

      next({ ...to, replace: true })
    } catch (error) {
      console.error('Failed to initialize dynamic routes:', error)
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      next({ name: 'Login' })
    }
    return
  }

  if (token && to.name === 'Login') {
    /**
     * 动态路由已经就绪时，已登录用户再次进入登录页需要回到首页。
     */
    next({ path: '/home', replace: true })
    return
  }

  next()
})

export default router
